import { array, check, type InferOutput, object, picklist, pipe } from "valibot";
import { errorTranslation, optionalTextValue, requireText, uniqueItems } from "../../../utils/validation/base";
import {
  MAX_CHARACTERS_MD,
  MAX_CHARACTERS_SM,
  URL_DATA_SOURCE_MAX_PARAMETERS,
  URL_DATA_SOURCE_MAX_REQUEST_HEADERS,
} from "../../../constants";
import type { DataSourceDefinition } from "../../../types/DataSource";
import { resolveUpdateInterval, UPDATE_INTERVAL_OPTIONS, UPDATE_INTERVALS } from "../../../types/DataSource";
import { useTranslation } from "../../../state/i18n";
import { CompactTextInput, TextInput } from "../../../components/inputs/TextInput";
import { Toggle } from "../../../components/toggle/Toggle";
import { createEffect, createSignal, createUniqueId, Show, untrack, type VoidComponent } from "solid-js";
import { PasswordInput } from "../../../components/inputs/PasswordInput";
import FeatureService from "../../../services/FeatureService";
import { SingleSelectInput } from "../../../components/menu/Select";
import { Checkbox } from "../../../components/checkbox/Checkbox";
import { isEmpty, isNil } from "lodash-es";
import Input from "../../../components/inputs/Input";
import { IconButton, TextAndIconButton } from "../../../components/button/Button";
import InlineMessage from "../../../components/inlineMessage/InlineMessage";
import { keyFromSelector } from "i18next";
import { createWritableMemo } from "@solid-primitives/memo";
import { baseDataSourceSchema } from "../../../utils/validation/dataSource";
import { RefreshDataSourceButton } from "./RefreshDataSourceButton";
import { Key } from "@solid-primitives/keyed";
import uuid from "../../../utils/uuid";

const schema = object({
  ...baseDataSourceSchema.entries,
  url: requireText(MAX_CHARACTERS_MD),
  username: optionalTextValue(MAX_CHARACTERS_SM),
  password: optionalTextValue(MAX_CHARACTERS_SM),
  parameters: pipe(
    array(object({ key: optionalTextValue(MAX_CHARACTERS_SM), value: optionalTextValue(MAX_CHARACTERS_SM) })),
    check(uniqueItems("key"), errorTranslation(keyFromSelector(($) => $.DATASOURCE_KV_INPUT_VALIDATION_UNIQUE_KEY))),
  ),
  requestHeaders: pipe(
    array(object({ key: optionalTextValue(MAX_CHARACTERS_SM), value: optionalTextValue(MAX_CHARACTERS_SM) })),
    check(uniqueItems("key"), errorTranslation(keyFromSelector(($) => $.DATASOURCE_KV_INPUT_VALIDATION_UNIQUE_KEY))),
  ),
  updateInterval: picklist(UPDATE_INTERVALS),
});

export type UrlDataSourceConfigurationDto = {
  urlConfiguration: {
    url: string;
    credentials?: {
      username?: string;
      password?: string;
    };
    parameters: Record<string, string>;
    requestHeaders: Record<string, string>;
    updateInterval: string;
  };
};

const UrlDataSource: DataSourceDefinition<InferOutput<typeof schema>, UrlDataSourceConfigurationDto> = {
  name: keyFromSelector(($) => $.DATASOURCES_URL_NAME),
  type: "URL",
  icon: "link",
  schema,
  initialValue: {
    name: undefined,
    url: undefined,
    parameters: [],
    requestHeaders: [],
    updateInterval: "NEVER",
    availableOffline: false,
  },
  toConfiguration: (input) => {
    if (isNil(input.urlConfiguration)) {
      throw new Error("Invalid datasource configuration");
    }

    return {
      name: input.name,
      url: input.urlConfiguration.url,
      username: input.urlConfiguration.credentials?.username,
      password: input.urlConfiguration.credentials?.password,
      parameters: Object.entries(input.urlConfiguration?.parameters ?? {}).map(([key, value]) => ({ key, value })),
      requestHeaders: Object.entries(input.urlConfiguration?.requestHeaders ?? {}).map(([key, value]) => ({
        key,
        value,
      })),
      updateInterval: resolveUpdateInterval(input.urlConfiguration.updateInterval),
      availableOffline: input.searchType === "LOCAL_FALLBACK",
    };
  },
  toDto: (input) => {
    const { name, availableOffline, ...configuration } = input;
    const credentials =
      isNil(configuration.username) && isNil(configuration.password)
        ? undefined
        : {
            username: configuration.username,
            password: configuration.password,
          };

    const filterNilKeys = (item: { key?: string; value?: string }): item is { key: string; value?: string } =>
      !isNil(item.key);

    return {
      name,
      urlConfiguration: {
        url: configuration.url,
        credentials,
        parameters: configuration.parameters
          .filter(filterNilKeys)
          .reduce((acc, item) => ({ ...acc, [item.key]: item.value }), {}),
        requestHeaders: configuration.requestHeaders
          .filter(filterNilKeys)
          .reduce((acc, item) => ({ ...acc, [item.key]: item.value }), {}),
        updateInterval: configuration.updateInterval,
      },
      searchType: availableOffline ? "LOCAL_FALLBACK" : "LIVE",
    };
  },
  extraAction: (props) => <RefreshDataSourceButton {...props} />,
  EditItem: (props) => {
    const { t } = useTranslation();
    const [hasBasicAuth, setHasBasicAuth] = createWritableMemo(
      () => !isEmpty(props.value("username")) || !isEmpty(props.value("password")),
    );

    return (
      <div class="flex flex-col gap-6">
        <TextInput
          label={t(($) => $.DATASOURCE_INPUT_NAME)}
          value={props.value("name")}
          required
          onBlur={(e) => props.updateValue("name", e.target.value)}
          onInput={(e) => props.updateValue("name", e.target.value)}
          errorMessage={props.error("name")}
        />

        <TextInput
          label={t(($) => $.DATASOURCE_URL_INPUT_URL)}
          value={props.value("url")}
          required
          onBlur={(e) => props.updateValue("url", e.target.value)}
          onInput={(e) => props.updateValue("url", e.target.value)}
          errorMessage={props.error("url")}
        />

        <div>
          <div class="border-b border-subtle pb-3">
            <Toggle
              block
              label={t(($) => $.DATASOURCE_URL_TOGGLE_AUTH_LABEL)}
              checked={hasBasicAuth()}
              setChecked={setHasBasicAuth}
            />
          </div>

          <Show when={hasBasicAuth()}>
            <div class="flex flex-col gap-4 bg-surface-extra-subtle p-4">
              <TextInput
                label={t(($) => $.DATASOURCE_URL_INPUT_USERNAME)}
                value={props.value("username")}
                onBlur={(e) => props.updateValue("username", e.target.value)}
                onInput={(e) => props.updateValue("username", e.target.value)}
                errorMessage={props.error("username")}
              />
              <PasswordInput
                label={t(($) => $.DATASOURCE_URL_INPUT_PASSWORD)}
                value={props.value("password")}
                onBlur={(e) => props.updateValue("password", e.target.value)}
                onInput={(e) => props.updateValue("password", e.target.value)}
                errorMessage={props.error("password")}
              />
            </div>
          </Show>
        </div>

        <KeyValueInput
          label={t(($) => $.DATASOURCE_URL_INPUT_PARAMETERS)}
          buttonLabel={t(($) => $.DATASOURCE_URL_INPUT_PARAMETERS_ADD)}
          limitReachedMessage={t(($) => $.DATASOURCE_URL_INPUT_PARAMETERS_LIMIT, {
            limit: URL_DATA_SOURCE_MAX_PARAMETERS,
          })}
          maxItems={URL_DATA_SOURCE_MAX_PARAMETERS}
          value={props.value("parameters") ?? []}
          onChange={(value) => props.updateValue("parameters", value)}
          errorMessage={props.error("parameters")}
        />

        <KeyValueInput
          label={t(($) => $.DATASOURCE_URL_INPUT_REQUEST_HEADERS)}
          buttonLabel={t(($) => $.DATASOURCE_URL_INPUT_REQUEST_HEADERS_ADD)}
          limitReachedMessage={t(($) => $.DATASOURCE_URL_INPUT_REQUEST_HEADERS_LIMIT, {
            limit: URL_DATA_SOURCE_MAX_REQUEST_HEADERS,
          })}
          maxItems={URL_DATA_SOURCE_MAX_REQUEST_HEADERS}
          value={props.value("requestHeaders") ?? []}
          onChange={(value) => props.updateValue("requestHeaders", value)}
          errorMessage={props.error("requestHeaders")}
        />

        <Show when={FeatureService.hasFeature("AUTOMATIC_DATASOURCES")}>
          <SingleSelectInput
            label={t(($) => $.DATASOURCE_URL_INPUT_UPDATE_INTERVAL)}
            value={props.value("updateInterval")}
            onSelect={(value) => props.updateValue("updateInterval", value)}
            options={UPDATE_INTERVAL_OPTIONS.map(({ value, label }) => ({
              variant: "text",
              value,
              label: t(label),
            }))}
            sameWidth
            errorMessage={props.error("updateInterval")}
          />
        </Show>

        <Checkbox
          label={t(($) => $.DATASOURCE_INPUT_AVAILABLE_OFFLINE)}
          checked={props.value("availableOffline") === true}
          setChecked={(value) => props.updateValue("availableOffline", value)}
        />
      </div>
    );
  },
};

type KeyValue = { key?: string; value?: string };
type KeyValueInputProps = {
  label: string;
  buttonLabel: string;
  limitReachedMessage: string;
  maxItems: number;
  value: KeyValue[];
  onChange: (value: KeyValue[]) => void;
  errorMessage?: string;
};
const KeyValueInput: VoidComponent<KeyValueInputProps> = (props) => {
  const { t } = useTranslation();
  const [value, setValue] = createSignal<(KeyValue & { id: string })[]>(
    untrack(() => props.value).map((item) => ({ id: uuid(), ...item })),
  );
  createEffect(() => {
    props.onChange(value());
  });

  const addEntry = () => setValue((prev) => [...prev, { id: uuid() }]);
  const updateEntry = (index: number, key: "key" | "value", value: string) =>
    setValue((prev) => prev.map((item, i) => (i === index ? { ...item, [key]: value } : item)));
  const removeEntry = (index: number) => setValue((prev) => prev.filter((_, i) => i !== index));

  return (
    <div
      class="flex flex-col"
      classList={{
        "gap-1": isEmpty(value()),
        "gap-2": !isEmpty(value()),
      }}
    >
      <Input label={props.label} errorMessage={props.errorMessage}>
        {() => (
          <Show when={!isEmpty(value())}>
            <ul class="flex flex-col gap-1">
              <Key each={value()} by={(item) => item.id}>
                {(item, index) => (
                  <li class="flex gap-2">
                    <CompactTextInput
                      id={createUniqueId()}
                      value={item().key}
                      placeholder={t(($) => $.DATASOURCE_KV_INPUT_KEY_PLACEHOLDER)}
                      onBlur={(e) => updateEntry(index(), "key", e.target.value)}
                    />
                    <CompactTextInput
                      id={createUniqueId()}
                      value={item().value}
                      placeholder={t(($) => $.DATASOURCE_KV_INPUT_VALUE_PLACEHOLDER)}
                      onBlur={(e) => updateEntry(index(), "value", e.target.value)}
                    />
                    <IconButton
                      variant="tertiary"
                      size="large"
                      icon="trash"
                      aria-label={t(($) => $.DELETE_ARIA)}
                      onClick={() => removeEntry(index())}
                    />
                  </li>
                )}
              </Key>
            </ul>
          </Show>
        )}
      </Input>
      <Show
        when={value().length < props.maxItems}
        fallback={<InlineMessage variant="warning" label={props.limitReachedMessage} />}
      >
        <TextAndIconButton variant="light" size="large" text={props.buttonLabel} icon="plus" onClick={addEntry} />
      </Show>
    </div>
  );
};

export default UrlDataSource;
